import React, { useMemo, useState } from 'react';
import { Button, Center, Divider, HStack, SimpleGrid, Text, VStack } from '@televet/kibble-ui';
import { PhoneCallState } from 'shared/providers/PhoneCallProvider/interfaces/PhoneCallState';
import { PetAvatar } from 'shared/components/Avatars';
import partial from 'lodash-es/partial';
import { formatPetAge } from 'shared/utils/formatPetAge';
import PhoneCallParticipantEventList from './PhoneCallParticipantEventList';
import { PhoneCallParticipantEvent } from 'shared/providers/PhoneCallProvider/interfaces/PhoneCallParticipantEvent';
import { isPast } from 'date-fns';
import PetParentConversationCard from 'shared/components/PetParentConversationCard';

interface PhoneCallParticipantDetailsContainerProps {
  phoneCallState: PhoneCallState;
  onPetClick: (petId: string) => void;
  onConversationClick: (channelId: string) => void;
}

const PhoneCallParticipantDetails = ({
  phoneCallState,
  onPetClick,
  onConversationClick,
}: PhoneCallParticipantDetailsContainerProps): JSX.Element => {
  const [isPetListExpanded, setIsPetListExpanded] = useState(false);

  const upcomingEvents: PhoneCallParticipantEvent[] = useMemo(() => {
    const events = phoneCallState.participant?.events;
    if (!events) {
      return [];
    }
    return events
      .filter((event) => !isPast(event.timestamp))
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }, [phoneCallState.participant?.events]);

  const historyEvents: PhoneCallParticipantEvent[] = useMemo(() => {
    const events = phoneCallState.participant?.events;
    if (!events) {
      return [];
    }
    return events
      .filter((event) => isPast(event.timestamp))
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }, [phoneCallState.participant?.events]);

  return (
    <VStack className="PhoneCallParticipantDetails" spacing={4} pb={1} align="stretch">
      <VStack className="PhoneCallPopover__PetList" align="stretch" spacing={2}>
        <SimpleGrid columns={2} spacingX={6} spacingY={3} px={4} py={1}>
          {phoneCallState.participant.pets.slice(0, isPetListExpanded ? undefined : 4).map((pet) => (
            <HStack key={pet.id} flex="0 1 auto" overflow="hidden" spacing={2} justify="flex-start" align="center">
              <PetAvatar
                size="md"
                speciesType={pet.speciesType}
                isDeceased={false}
                m={0}
                cursor="pointer"
                onClick={partial(onPetClick, pet.id)}
                _hover={{ backgroundColor: 'background.mostSubtleContrast' }}
              />
              <VStack align="flex-start" spacing={0} overflow="hidden">
                <Text
                  size="lg"
                  fontWeight="bold"
                  lineHeight={1.2}
                  w="100%"
                  textAlign="left"
                  overflow="hidden"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                >
                  {pet.name}
                </Text>
                <Text fontWeight="normal" size="sm" lineHeight={1.2}>
                  {`${pet.gender ?? ''}${pet.dateOfBirth ? ` • ${formatPetAge(pet.dateOfBirth)}` : ''}`}
                </Text>
              </VStack>
            </HStack>
          ))}
        </SimpleGrid>
        {phoneCallState.participant.pets.length > 4 && (
          <Center>
            <Button variant="tertiary" size="xs" onClick={(): void => setIsPetListExpanded((value) => !value)}>
              {isPetListExpanded ? 'Hide' : 'Show'} more
            </Button>
          </Center>
        )}
      </VStack>
      <PhoneCallParticipantEventList
        label="Upcoming"
        description="Next 14 days"
        emptyStateText="No upcoming events"
        events={upcomingEvents}
      />
      <PhoneCallParticipantEventList
        label="History"
        description="Last 14 days"
        emptyStateText="No recent events"
        events={historyEvents}
      />
      <Divider />
      <VStack align="stretch" spacing={2} px={4}>
        <Text fontWeight="semibold" fontSize="md">
          Conversation
        </Text>
        {phoneCallState.participant?.channel ? (
          <PetParentConversationCard channel={phoneCallState.participant?.channel} onCardClick={onConversationClick} />
        ) : (
          <Text color="text.subtle">No recent conversation</Text>
        )}
      </VStack>
    </VStack>
  );
};

export default PhoneCallParticipantDetails;
